/**
 * Entitlements & Packaging Engine
 *
 * Priorities.ai is sold as an organizational license (see
 * marketing-site/src/content/marketing/pricing.md). Until now that license
 * existed only as marketing copy — there was no place in the product where a
 * workspace's plan, contract term, and purchased add-ons were represented, and
 * nothing that could answer "is this customer entitled to this capability?".
 *
 * This module is that representation. It is the commercial counterpart to the
 * feature-flag system (`featureFlags.ts`):
 *
 *   - Feature flags answer "has this capability been *rolled out* to this user?"
 *     (a delivery/rollout concern, user-scoped).
 *   - Entitlements answer "has this workspace *purchased* this capability?"
 *     (a commercial concern, workspace-scoped, contract-bound).
 *
 * A capability is usable only when BOTH are true: the workspace is entitled to
 * it AND the feature flag is enabled. See `isCapabilityAvailable()`.
 *
 * Pure, dependency-free, and unit-tested so it can run on the server (Edge
 * Functions / RPC layer) and the client. Persistence lives in the
 * `workspace_plans` table (migration 497); this module is the decision logic.
 */

// ============================================================================
// Plan tiers
// ============================================================================

/**
 * The three commercial tiers. These mirror the published packaging:
 *   - pilot:        a time-boxed, capped proof-of-value engagement
 *   - organization: the standard $150k/yr organizational license
 *   - enterprise:   organization license + negotiated compliance/infra add-ons
 */
export type PlanTier = 'pilot' | 'organization' | 'enterprise';

/** Contract / subscription lifecycle state. */
export type PlanStatus = 'trialing' | 'active' | 'past_due' | 'suspended' | 'cancelled';

/**
 * Add-ons are separately-quoted capabilities layered on top of a base license
 * (see the "Enterprise Add-Ons" table in pricing.md). They are boolean grants.
 */
export type AddOnKey =
  | 'scim_provisioning'
  | 'data_residency'
  | 'soc2_report'
  | 'custom_connector'
  | 'dedicated_infrastructure'
  | 'hmac_webhook_signing'
  | 'audit_log_api'
  | 'ip_allowlisting'
  | 'priority_support';

// ============================================================================
// Entitlement keys
// ============================================================================

/**
 * The full set of gateable capabilities. Boolean entitlements are capabilities
 * a plan either has or doesn't; numeric limits are expressed separately.
 */
export type EntitlementKey =
  // Core platform (every paying plan)
  | 'prioritization_cycles'
  | 'planning_domain'
  | 'catchball'
  | 'workspace_admin_console'
  | 'rest_api'
  | 'mcp_endpoint'
  | 'connectors'
  | 'webhooks'
  | 'audit_governance_tables'
  // Decision Brief (standalone wedge product)
  | 'decision_brief'
  | 'decision_brief_unlimited'
  // Identity
  | 'sso_saml'
  | 'scim_provisioning'
  | 'mfa_enforcement'
  // Compliance & data
  | 'audit_log_api'
  | 'data_residency'
  | 'soc2_report'
  | 'hmac_webhook_signing'
  | 'ip_allowlisting'
  | 'dedicated_infrastructure'
  // Commercial / service
  | 'guided_implementation'
  | 'priority_support'
  | 'custom_connector_development'
  // AI / LLM assists and workspace agents (commercial gate)
  | 'ai_features'
  // Judgment Analytics — the decision-intelligence / judgment-taxonomy module.
  // Calibration, non-consensus edge, judgment-lens profiling and the leader
  // trust map. Advisory: never alters rankings or authority state.
  | 'judgment_analytics'
  // App Layer — one commercial key per consumer app (PRD-app-layer FR-6).
  // Granted on every current tier; the keys exist so a future portfolio plan
  // can price per-unit app entitlements independently of the full license.
  | 'app_session'
  | 'app_rank'
  | 'app_cut'
  | 'app_items'
  | 'app_viability'
  | 'app_focus'
  | 'app_intake'
  | 'app_review'
  | 'app_classification'
  | 'app_relationships'
  | 'app_align'
  | 'app_allocate'
  | 'app_offsite'
  | 'app_hire'
  | 'app_vendor_select'
  | 'app_risk_triage'
  | 'app_post_mortem'
  | 'app_ask_the_room'
  | 'app_start'
  | 'app_session_import'
  | 'app_decisions'
  | 'app_mandate'
  | 'app_whats_next'
  | 'app_first_100_days';

/**
 * Numeric usage limits. `null` means unlimited (the organizational license is
 * deliberately unlimited on users/sessions/cycles — see pricing.md). Limits are
 * primarily meaningful on the capped `pilot` tier.
 */
export interface PlanLimits {
  maxUsers: number | null;
  maxActiveCycles: number | null;
  maxSessionsPerMonth: number | null;
  maxConnectors: number | null;
  /** Maximum active (non-archived) Decision Briefs. null = unlimited. */
  maxBriefs: number | null;
}

// ============================================================================
// Decision Brief tier
// ============================================================================

/**
 * Decision Brief is the standalone wedge product.  It has its own thin tier
 * model independent of the main platform tiers:
 *
 *   brief_free   — up to FREE_BRIEF_LIMIT active briefs, core brief surface
 *   brief_pro    — unlimited briefs, AI conflict detection, export connectors
 *
 * Any workspace on the organization or enterprise platform plan gets
 * brief_pro automatically (Decision Brief is bundled in).
 */
export type BriefTier = 'brief_free' | 'brief_pro';

/** Maximum active briefs on the free Decision Brief tier. */
export const FREE_BRIEF_LIMIT = 3;

/**
 * Resolves the effective Brief tier for a workspace.  Organization and
 * enterprise platform plans bundle brief_pro.  Pilot and unplanned workspaces
 * default to brief_free.
 */
export function resolveBriefTier(plan: WorkspacePlanRecord): BriefTier {
  if (plan.tier === 'organization' || plan.tier === 'enterprise') return 'brief_pro';
  return 'brief_free';
}

/**
 * Whether a workspace can create another active brief given its current usage.
 * Pass `activeBriefCount` from `get_workspace_brief_count()` RPC.
 */
export function canCreateBrief(plan: WorkspacePlanRecord, activeBriefCount: number): boolean {
  if (!isPlanActive(plan)) return false;
  if (resolveBriefTier(plan) === 'brief_pro') return true;
  return activeBriefCount < FREE_BRIEF_LIMIT;
}

/** A fully-resolved entitlement set for a workspace at a point in time. */
export interface EntitlementSet {
  tier: PlanTier;
  status: PlanStatus;
  entitlements: Record<EntitlementKey, boolean>;
  limits: PlanLimits;
  /** True when the contract is in a state that permits product use. */
  active: boolean;
  /** ISO date the current term ends, if known. */
  termEndsAt?: string;
}

/**
 * The persisted commercial record for a workspace (shape of `workspace_plans`).
 *
 * Named `WorkspacePlanRecord` (not `WorkspacePlan`) to avoid collision with the
 * legacy `WorkspacePlan = 'free' | 'starter' | …` string union in
 * `workspaceService.ts`, which tracks the old `workspaces.plan` column.
 */
export interface WorkspacePlanRecord {
  workspaceId: string;
  tier: PlanTier;
  status: PlanStatus;
  addOns: AddOnKey[];
  /** Contracted seat count, informational only on unlimited plans. */
  seats?: number;
  termStartsAt?: string;
  termEndsAt?: string;
}

// ============================================================================
// Plan definitions
// ============================================================================

const ALL_ENTITLEMENT_KEYS: EntitlementKey[] = [
  'prioritization_cycles',
  'planning_domain',
  'catchball',
  'workspace_admin_console',
  'rest_api',
  'mcp_endpoint',
  'connectors',
  'webhooks',
  'audit_governance_tables',
  'decision_brief',
  'decision_brief_unlimited',
  'sso_saml',
  'scim_provisioning',
  'mfa_enforcement',
  'audit_log_api',
  'data_residency',
  'soc2_report',
  'hmac_webhook_signing',
  'ip_allowlisting',
  'dedicated_infrastructure',
  'guided_implementation',
  'priority_support',
  'custom_connector_development',
  'ai_features',
  'judgment_analytics',
  'app_session',
  'app_rank',
  'app_cut',
  'app_items',
  'app_viability',
  'app_focus',
  'app_intake',
  'app_review',
  'app_classification',
  'app_relationships',
  'app_align',
  'app_allocate',
  'app_offsite',
  'app_hire',
  'app_vendor_select',
  'app_risk_triage',
  'app_post_mortem',
  'app_ask_the_room',
  'app_start',
  'app_session_import',
  'app_decisions',
  'app_mandate',
  'app_whats_next',
  'app_first_100_days',
];

interface PlanDefinition {
  /** Entitlements granted by the base plan, before add-ons. */
  baseEntitlements: EntitlementKey[];
  limits: PlanLimits;
}

const PLAN_DEFINITIONS: Record<PlanTier, PlanDefinition> = {
  // Pilot: capped proof-of-value. Core product, no enterprise identity/compliance.
  pilot: {
    baseEntitlements: [
      'prioritization_cycles',
      'catchball',
      'workspace_admin_console',
      'rest_api',
      'audit_governance_tables',
      'sso_saml',
      'guided_implementation',
      'decision_brief',  // pilot gets the free-tier brief surface
      'ai_features',
      'app_session',
      'app_rank',
      'app_cut',
      'app_items',
      'app_viability',
      'app_focus',
      'app_intake',
      'app_review',
      'app_classification',
      'app_relationships',
      'app_align',
      'app_allocate',
      'app_offsite',
      'app_hire',
      'app_vendor_select',
      'app_risk_triage',
      'app_post_mortem',
      'app_ask_the_room',
      'app_start',
      'app_session_import',
      'app_decisions',
      'app_mandate',
      'app_whats_next',
      'app_first_100_days',
    ],
    limits: {
      maxUsers: 25,
      maxActiveCycles: 2,
      maxSessionsPerMonth: 50,
      maxConnectors: 2,
      maxBriefs: FREE_BRIEF_LIMIT,
    },
  },

  // Organization: the standard $150k/yr license. Unlimited, full product surface.
  organization: {
    baseEntitlements: [
      'prioritization_cycles',
      'planning_domain',
      'catchball',
      'workspace_admin_console',
      'rest_api',
      'mcp_endpoint',
      'connectors',
      'webhooks',
      'audit_governance_tables',
      'sso_saml',
      'guided_implementation',
      'decision_brief',
      'decision_brief_unlimited',
      'ai_features',
      'judgment_analytics',
      'app_session',
      'app_rank',
      'app_cut',
      'app_items',
      'app_viability',
      'app_focus',
      'app_intake',
      'app_review',
      'app_classification',
      'app_relationships',
      'app_align',
      'app_allocate',
      'app_offsite',
      'app_hire',
      'app_vendor_select',
      'app_risk_triage',
      'app_post_mortem',
      'app_ask_the_room',
      'app_start',
      'app_session_import',
      'app_decisions',
      'app_mandate',
      'app_whats_next',
      'app_first_100_days',
    ],
    limits: {
      maxUsers: null,
      maxActiveCycles: null,
      maxSessionsPerMonth: null,
      maxConnectors: null,
      maxBriefs: null,
    },
  },

  // Enterprise: organization license as a baseline; compliance/infra capabilities
  // are unlocked via add-ons rather than granted wholesale, so the entitlement
  // set always reflects what was actually contracted.
  enterprise: {
    baseEntitlements: [
      'prioritization_cycles',
      'planning_domain',
      'catchball',
      'workspace_admin_console',
      'rest_api',
      'mcp_endpoint',
      'connectors',
      'webhooks',
      'audit_governance_tables',
      'sso_saml',
      'mfa_enforcement',
      'guided_implementation',
      'priority_support',
      'decision_brief',
      'decision_brief_unlimited',
      'ai_features',
      'judgment_analytics',
      'app_session',
      'app_rank',
      'app_cut',
      'app_items',
      'app_viability',
      'app_focus',
      'app_intake',
      'app_review',
      'app_classification',
      'app_relationships',
      'app_align',
      'app_allocate',
      'app_offsite',
      'app_hire',
      'app_vendor_select',
      'app_risk_triage',
      'app_post_mortem',
      'app_ask_the_room',
      'app_start',
      'app_session_import',
      'app_decisions',
      'app_mandate',
      'app_whats_next',
      'app_first_100_days',
    ],
    limits: {
      maxUsers: null,
      maxActiveCycles: null,
      maxSessionsPerMonth: null,
      maxConnectors: null,
      maxBriefs: null,
    },
  },
};

/** Maps a purchased add-on to the entitlement(s) it unlocks. */
const ADDON_ENTITLEMENTS: Record<AddOnKey, EntitlementKey[]> = {
  scim_provisioning: ['scim_provisioning'],
  data_residency: ['data_residency'],
  soc2_report: ['soc2_report'],
  custom_connector: ['custom_connector_development'],
  dedicated_infrastructure: ['dedicated_infrastructure'],
  hmac_webhook_signing: ['hmac_webhook_signing'],
  audit_log_api: ['audit_log_api'],
  ip_allowlisting: ['ip_allowlisting'],
  priority_support: ['priority_support'],
};

/** Statuses under which the product remains usable. */
const USABLE_STATUSES: ReadonlySet<PlanStatus> = new Set<PlanStatus>([
  'trialing',
  'active',
  'past_due', // grace period — usable, but flagged for dunning
]);

// ============================================================================
// Resolution
// ============================================================================

/**
 * Resolves a workspace's full entitlement set from its persisted plan record.
 * This is the single source of truth for "what has this customer bought?".
 */
export function resolveEntitlements(plan: WorkspacePlanRecord): EntitlementSet {
  const definition = PLAN_DEFINITIONS[plan.tier];
  const granted = new Set<EntitlementKey>(definition.baseEntitlements);

  for (const addOn of plan.addOns ?? []) {
    const unlocked = ADDON_ENTITLEMENTS[addOn];
    if (unlocked) {
      for (const key of unlocked) granted.add(key);
    }
  }

  const entitlements = ALL_ENTITLEMENT_KEYS.reduce((acc, key) => {
    acc[key] = granted.has(key);
    return acc;
  }, {} as Record<EntitlementKey, boolean>);

  const active = isPlanActive(plan);

  return {
    tier: plan.tier,
    status: plan.status,
    entitlements,
    limits: definition.limits,
    active,
    termEndsAt: plan.termEndsAt,
  };
}

/**
 * Whether the workspace's contract permits product use right now. Suspended and
 * cancelled plans are locked out; an expired term (termEndsAt in the past) is
 * also treated as inactive regardless of status field.
 *
 * @param now injectable clock for deterministic tests.
 */
export function isPlanActive(plan: WorkspacePlanRecord, now: Date = new Date()): boolean {
  if (!USABLE_STATUSES.has(plan.status)) return false;
  if (plan.termEndsAt) {
    const end = new Date(plan.termEndsAt);
    if (!Number.isNaN(end.getTime()) && end.getTime() < now.getTime()) {
      return false;
    }
  }
  return true;
}

/**
 * Whether a workspace is entitled to a capability. Returns false for any
 * capability when the plan is not active, so an expired/suspended contract
 * cleanly closes the whole surface.
 */
export function hasEntitlement(plan: WorkspacePlanRecord, key: EntitlementKey): boolean {
  if (!isPlanActive(plan)) return false;
  return resolveEntitlements(plan).entitlements[key] === true;
}

/**
 * The commercial gate combined with the rollout gate. A capability is usable
 * only when the workspace has purchased it AND the feature has been rolled out.
 *
 * Pass the feature-flag result in rather than importing it, so this stays a
 * pure function usable on both client and server.
 */
export function isCapabilityAvailable(
  plan: WorkspacePlanRecord,
  key: EntitlementKey,
  featureFlagEnabled = true,
): boolean {
  return hasEntitlement(plan, key) && featureFlagEnabled === true;
}

/**
 * Enforces a numeric limit. Returns whether the requested usage is within the
 * plan's allowance (unlimited plans always pass).
 */
export function isWithinLimit(
  plan: WorkspacePlanRecord,
  limit: keyof PlanLimits,
  currentUsage: number,
): boolean {
  if (!isPlanActive(plan)) return false;
  const allowance = PLAN_DEFINITIONS[plan.tier].limits[limit];
  if (allowance === null) return true; // unlimited
  return currentUsage < allowance;
}

/**
 * A safe default for workspaces with no plan record yet (e.g. the auto-provisioned
 * demo workspace). Treated as an active pilot so the product is explorable, but
 * with no enterprise identity/compliance entitlements.
 */
export function defaultPlan(workspaceId: string): WorkspacePlanRecord {
  return {
    workspaceId,
    tier: 'pilot',
    status: 'trialing',
    addOns: [],
  };
}

/** The add-ons that are commercially offered, for rendering quote/order forms. */
export const OFFERED_ADDONS: AddOnKey[] = Object.keys(ADDON_ENTITLEMENTS) as AddOnKey[];

/** Human-readable labels for plans and add-ons (UI / proposals / invoices). */
export const PLAN_LABELS: Record<PlanTier, string> = {
  pilot: 'Pilot',
  organization: 'Organizational License',
  enterprise: 'Enterprise',
};

export const ADDON_LABELS: Record<AddOnKey, string> = {
  scim_provisioning: 'SCIM Provisioning',
  data_residency: 'Data Residency (EU / APAC)',
  soc2_report: 'SOC 2 Type II Report',
  custom_connector: 'Custom Connector Development',
  dedicated_infrastructure: 'Dedicated (Single-Tenant) Infrastructure',
  hmac_webhook_signing: 'HMAC-SHA256 Webhook Signing',
  audit_log_api: 'Audit Log API',
  ip_allowlisting: 'IP Allowlisting',
  priority_support: 'Priority Support & SLA',
};
